import {
  Box,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { HubCard } from "~/components/hub-card";
import { SpaceOrWorkspaceScope, SpaceScope } from "~/hooks/useScope";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Flex } from "~/components/Flex";
import { useTopActionsProgressCard } from "./hooks/useTopActionsProgressCard";
import { Link } from "react-router-dom";
import { TicketsTitleCell } from "~/pages/tickets/tickets/TicketsTable/TicketsTitleCell";
import { CompletionBar } from "~/components/Completion/CompletionBar";
import { FormatDate, FormatRelativeDate } from "~/lib/date";
import TopActionsCardSpaceRiskCell from "~/components/FirewatchRow/TopActionsCardSpaceRiskCell";
import { Paginate } from "~/components/pagination/paginate";
import { DataTable } from "~/components/data-table";
import { TicketRefType } from "~/operations/types";
import { ChecklistIcon } from "~/icons";
import { TopActionsProgressCardSkeleton } from "~/pages/space/home-dashboard/TopActionsProgressCard/components/TopActionsProgressCardSkeleton";
import TopActionsProgressCardEmptyState from "~/pages/space/home-dashboard/TopActionsProgressCard/components/TopActionsProgressCardEmptyState";

interface TopActionsCardProps {
  scope: SpaceOrWorkspaceScope;
  spaceScope: SpaceScope;
}

export function TopActionsProgressCard({ scope }: TopActionsCardProps) {
  const theme = useTheme();
  const {
    tickets,
    paginateProps,
    loading,
    tableHeaders,
    isEmpty,
    handleSortClick,
    orderBy,
  } = useTopActionsProgressCard(scope);

  const tagline = isEmpty ? (
    <></>
  ) : (
    <Flex
      sx={{
        color: "text.secondary",
        alignItems: "center",
        gap: 0.5,
        mt: 0.8,
        textTransform: "initial",
      }}
    >
      <Typography
        color="text.secondary"
        textTransform="uppercase"
        fontSize={12}
      >
        tickets that have been created for top actions
      </Typography>
    </Flex>
  );

  const tooltip = (
    <Typography variant="caption">
      This widget tracks the status of remediation work that has been committed
      to by your team.
      <ul style={{ paddingLeft: "16px" }}>
        <li>
          <strong>How it works:</strong> This list <strong>only</strong> shows
          tickets that were created directly from the 'Top Actions for Risk
          Reduction' widget. It is not a general list of all open tickets.
        </li>
        <li>
          <strong>Scope:</strong> Its purpose is to monitor progress on the most
          critical tasks you've chosen to prioritize.
        </li>
      </ul>
    </Typography>
  );

  const title = (
    <Flex justifyContent="space-between" alignItems="flex-start">
      <Typography
        variant="h5"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          fontWeight: 700,
          mb: 0,
          fontSize: { xs: 20, md: 24 },
        }}
      >
        TOP ACTIONS IN PROGRESS
        <Tooltip title={tooltip} arrow placement="right">
          <InfoOutlinedIcon sx={{ color: "secondary.main", width: 16 }} />
        </Tooltip>
      </Typography>
    </Flex>
  );

  return (
    <HubCard
      loading={false}
      title={title}
      tagline={tagline}
      icon={<ChecklistIcon />}
      data-testid="top-actions-card"
      sx={{
        minHeight: 413,
        width: "100%",
      }}
    >
      <Flex justifyContent="space-between" flexDirection="column" height="100%">
        {loading && <TopActionsProgressCardSkeleton />}

        {isEmpty && <TopActionsProgressCardEmptyState loading={loading} />}

        {!loading && !isEmpty && (
          <DataTable firewatchSize="small">
            <TableHead
              sx={{
                backgroundColor: "inherit",
                "& th": {
                  fontSize: 10,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  borderBottom: "none",
                  pl: 0,
                  pb: "0 !important",
                },
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <TableRow>
                {tableHeaders.map((header) => {
                  const { label, options, id, sortable } = header;

                  return (
                    <TableCell
                      key={id}
                      align={options?.textAlign}
                      width={options?.width}
                      sx={{
                        fontWeight: "500 !important",
                        textWrap: "nowrap",
                      }}
                    >
                      {sortable ? (
                        <TableSortLabel
                          active={id === orderBy.field}
                          sx={{
                            "& .MuiTableSortLabel-icon": { fontSize: 10 },
                          }}
                          onClick={() => handleSortClick(id)}
                          direction={
                            orderBy.direction.toLowerCase() as "asc" | "desc"
                          }
                        >
                          {label}
                        </TableSortLabel>
                      ) : (
                        label
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((c) => {
                const percentage =
                  Math.floor((c.closedRefsCount / c.totalRefsCount) * 100) || 0;

                const ticketType =
                  c?.ticketRefs?.[0]?.type || TicketRefType.Unknown;

                return (
                  <TableRow
                    key={c.mrn}
                    component={Link}
                    to={`/space/ticket?${scope.params}&caseMrn=${c.mrn}`}
                    sx={{ td: { pl: 0 } }}
                    data-testid="top-action-progress-table-row"
                  >
                    <TicketsTitleCell
                      key={c.mrn}
                      isOpen
                      title={c.title}
                      assets={c.assetsCount}
                      createdAt={c.createdAt}
                      tags={c.tags || []}
                      isSmallTable
                      ticketType={ticketType}
                    />
                    <TableCell sx={{ py: 1 }}>
                      <Typography fontSize={12}>
                        {FormatRelativeDate(c.createdAt)} -{" "}
                        {FormatDate(c.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Flex
                        flex="1 0 auto"
                        justifyContent="flex-start"
                        sx={{ pr: 3 }}
                        alignItems="center"
                        className="progressBar"
                        gap={1}
                      >
                        <Flex flexDirection="column">
                          <Typography
                            color="primary"
                            fontSize={12}
                            fontWeight={600}
                          >
                            {percentage}%
                          </Typography>
                          <Box width="108px">
                            <CompletionBar
                              {...{
                                percent: percentage,
                                height: theme.spacing(1),
                                tooltip: `Progress: ${percentage}%`,
                              }}
                            />
                          </Box>
                        </Flex>
                      </Flex>
                    </TableCell>
                    <TopActionsCardSpaceRiskCell
                      spaceRiskImprovement={c.spaceRiskDelta || 0}
                    />
                    <TableCell>
                      <Typography fontSize={12}>{c.createdBy?.name}</Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </DataTable>
        )}
        {!isEmpty && paginateProps && (
          <Box mt={2}>
            <Paginate {...paginateProps} variant="small-table" />
          </Box>
        )}
      </Flex>
    </HubCard>
  );
}
